import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Camera, Save, Trash2, UserCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/store/auth-store';

type ProfileFormData = {
  name: string;
  email: string;
  avatarUrl: string | null;
};

export function ProfilePage() {
  const { user, updateProfile, isLoading } = useAuthStore();
  const [form, setForm] = useState<ProfileFormData>({
    name: '',
    email: '',
    avatarUrl: null,
  });

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || '',
      email: user.email || '',
      avatarUrl: user.avatarUrl || null,
    });
  }, [user]);

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const maxFileSize = 3 * 1024 * 1024;
    if (file.size > maxFileSize) {
      toast.error('La imagen es demasiado grande. Máximo 3 MB.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        avatarUrl: typeof reader.result === 'string' ? reader.result : null,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    await updateProfile({
      name: form.name.trim(),
      email: form.email.trim(),
      avatarUrl: form.avatarUrl || undefined,
    });
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <section className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900/80">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Modificar perfil</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Actualiza tu nombre, email y foto de perfil.
        </p>
      </section>

      <form
        onSubmit={handleSubmit}
        className="rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900/80"
      >
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
          <div className="relative">
            {form.avatarUrl ? (
              <img
                src={form.avatarUrl}
                alt="Avatar"
                className="h-24 w-24 rounded-full object-cover ring-4 ring-blue-100 dark:ring-blue-900/40"
              />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                <UserCircle2 className="h-16 w-16" />
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
              <Camera className="mr-2 h-4 w-4" />
              Subir foto
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => setForm((prev) => ({ ...prev, avatarUrl: null }))}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Quitar foto
            </Button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Nombre</label>
            <Input
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Nombre completo"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email</label>
            <Input
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              placeholder="usuario@email.com"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="submit" loading={isLoading}>
            <Save className="mr-2 h-4 w-4" />
            Guardar cambios
          </Button>
        </div>
      </form>
    </div>
  );
}
